import api from './api';
import { captureSession, assertCurrentSession, SESSION_EVENT } from '../utils/authSession';
import { storedHelpScope } from '../utils/helpScope';
const reads = new Set();
export const cancelHelpReads = () => {
  reads.forEach((controller) => controller.abort());
  reads.clear();
};
window.addEventListener(SESSION_EVENT, cancelHelpReads);

const value = (response) => response.data?.data ?? response.data;
// Bind every completion to the login that initiated it, including binary downloads.
async function call(method, url, data, options = {}) {
  const session = captureSession();
  const scope = storedHelpScope();
  const controller = method === 'get' || url.endsWith('/search') ? new AbortController() : null;
  if (controller) reads.add(controller);
  try {
    const response = await api.request({
      method,
      url,
      data,
      ...(controller ? { signal: controller.signal } : {}),
      ...options,
      sessionBinding: session,
    });
    assertCurrentSession(session);
    if (scope !== storedHelpScope() || controller?.signal.aborted)
      throw new Error('Your support access changed. Please reload.');
    return value(response);
  } finally {
    if (controller) reads.delete(controller);
  }
}
export const newHelpId = () => crypto.randomUUID();
const service = {
  config: () => call('get', '/support/config'),
  welcome: () => call('post', '/support/welcome'),
  search: (body, admin = false) =>
    call('post', `${admin ? '/admin' : ''}/support/requests/search`, body),
  create: (body) => call('post', '/support/requests', body),
  request: (id) => call('get', `/support/requests/${encodeURIComponent(id)}`),
  messages: (id, after = 0) =>
    call('get', `/support/requests/${encodeURIComponent(id)}/messages?after=${after}&limit=50`),
  reply: (id, body) => call('post', `/support/requests/${encodeURIComponent(id)}/messages`, body),
  read: (id, sequence) =>
    call('put', `/support/requests/${encodeURIComponent(id)}/read`, { sequence }),
  status: (id, status, expectedRevision) =>
    call('patch', `/support/requests/${encodeURIComponent(id)}/status`, {
      status,
      expectedRevision,
    }),
  assignees: () => call('get', '/admin/support/assignees'),
  assign: (id, assignedTo, expectedRevision) =>
    call('patch', `/admin/support/requests/${encodeURIComponent(id)}/assignment`, {
      assignedTo,
      expectedRevision,
    }),
  settings: (body, preview = false) =>
    call(preview ? 'post' : 'put', `/admin/support/settings${preview ? '/preview' : ''}`, body),
  upload: (file, clientUploadId, onProgress, signal) => {
    const form = new FormData();
    form.append('file', file);
    form.append('clientUploadId', clientUploadId);
    return call('post', '/support/uploads', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal,
      onUploadProgress: (event) =>
        onProgress(event.total ? Math.min(99, Math.round((event.loaded * 100) / event.total)) : 0),
    });
  },
  attachment: (id, attachmentId) =>
    call(
      'get',
      `/support/requests/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
      undefined,
      { responseType: 'blob' }
    ),
};
export default service;
